import { useTranslation } from "react-i18next";
import { fullName } from "../../utils/helpers/userDisplay";
import Avatar from "../Avatar/Avatar";

// Per-question split: YES / NO / NOT OBSERVED based on each teacher's latest
// finalized observation for the parent standard. Click a teacher chip to open
// the teacher overview modal.

function TeacherChip({ teacher, accent, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick?.(teacher)}
      className={`flex items-center gap-2 px-2.5 py-1 rounded-full bg-white border ${accent} hover:shadow-md transition-shadow cursor-pointer`}
    >
      <Avatar
        user={teacher}
        className="w-6 h-6 rounded-full bg-slate-700 text-white text-[9px] font-bold shrink-0"
      />
      <span className="text-xs font-medium text-slate-700 max-w-[140px] truncate">
        {teacher.full_name || fullName(teacher)}
      </span>
    </button>
  );
}

function DistributionColumn({ label, dotClass, borderClass, textClass, teachers, onTeacherClick }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className={`flex items-center gap-2 pb-2 border-b ${borderClass}`}>
        <div className={`w-2 h-2 rounded-full ${dotClass}`} />
        <span className={`text-xs font-bold uppercase tracking-tight ${textClass}`}>
          {label}
        </span>
        <span className="ml-auto text-xs font-semibold text-slate-400">
          {teachers.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {teachers.length > 0 ? (
          teachers.map((teacher) => (
            <TeacherChip
              key={teacher.uuid}
              teacher={teacher}
              accent={borderClass}
              onClick={onTeacherClick}
            />
          ))
        ) : (
          <span className="text-[10px] text-slate-500 italic">
            {t("standards.noTeachersInGroup")}
          </span>
        )}
      </div>
    </div>
  );
}

export default function TeacherDistribution({ groups, onTeacherClick }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      {groups.map(({ question, pass, fail, notObserved }) => (
        <div
          key={question.uuid}
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="bg-slate-200 text-slate-600 font-mono text-xs font-bold px-2 py-1 rounded border border-slate-300 shrink-0">
                {question.short_label ||
                  question.code ||
                  t("common.questionShort", { order: question.order })}
              </span>
              <h4 className="text-sm font-bold text-slate-800 truncate">
                {question.text}
              </h4>
            </div>
            <div className="flex gap-4 text-[10px] font-bold uppercase tracking-wider shrink-0">
              <span className="text-emerald-600">{t("common.yes")}: {pass.length}</span>
              <span className="text-rose-600">{t("common.no")}: {fail.length}</span>
              <span className="text-slate-400">{t("common.notObserved")}: {notObserved.length}</span>
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-8">
            <DistributionColumn
              label={t("common.yes")}
              dotClass="bg-emerald-500"
              borderClass="border-emerald-100"
              textClass="text-emerald-700"
              teachers={pass}
              onTeacherClick={onTeacherClick}
            />
            <DistributionColumn
              label={t("common.no")}
              dotClass="bg-rose-500"
              borderClass="border-rose-100"
              textClass="text-rose-700"
              teachers={fail}
              onTeacherClick={onTeacherClick}
            />
            <DistributionColumn
              label={t("common.notObserved")}
              dotClass="bg-slate-400"
              borderClass="border-slate-200"
              textClass="text-slate-500"
              teachers={notObserved}
              onTeacherClick={onTeacherClick}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
